import { gsap } from "gsap";
import type { Renderer } from "ogl";
import { Mesh, Program, Texture, Triangle } from "ogl";
import {
	MORPH_TRANSITIONS,
	type MorphTransition,
	morphFragmentShader,
	morphHexToRgb,
	morphVertexShader,
} from "./morph-shaders";
import { tryCreateOglRenderer } from "./try-create-ogl-renderer";

type GL = Renderer["gl"];

export interface MorphEngineItem {
	image: string;
}

export interface MorphEngineOptions {
	transition: MorphTransition;
	duration: number;
	ease: string;
	intensity: number;
	scale: number;
	aberration: number;
	drift: number;
	overlayColor: string;
	loop: boolean;
}

export interface MorphEngineConfig {
	items: MorphEngineItem[];
	startIndex: number;
	reducedMotion: boolean;
	getOptions: () => MorphEngineOptions;
	onIndexChange?: (index: number) => void;
	dprCap?: number;
	/**
	 * When true, canvas clears to transparent so a DOM photo underlay can
	 * cover resize / boot gaps without a black flash (MorphImage).
	 */
	alpha?: boolean;
}

function makeFallbackTexture(gl: GL): Texture {
	const size = 4;
	const data = new Uint8Array(size * size * 4);
	for (let i = 0; i < size * size; i++) {
		data[i * 4] = 24;
		data[i * 4 + 1] = 24;
		data[i * 4 + 2] = 28;
		data[i * 4 + 3] = 255;
	}
	return new Texture(gl, {
		image: data,
		width: size,
		height: size,
		generateMipmaps: false,
	});
}

/**
 * Shared WebGL morph engine — multi-item (slider) or 2-item (MorphImage).
 * Returns null when WebGL cannot be created.
 */
export class MorphEngine {
	private container: HTMLElement;
	private items: MorphEngineItem[];
	private getOptions: () => MorphEngineOptions;
	private onIndexChange: ((index: number) => void) | undefined;
	private reducedMotion: boolean;

	private current: number;
	private animating = false;
	private dragging = false;
	private dragDir = 0;
	private shownIndex: number;
	private tween: gsap.core.Tween | null = null;

	private renderer: Renderer;
	private gl: GL;
	private canvas: HTMLCanvasElement;
	private geometry: Triangle;
	private program: Program;
	private mesh: Mesh;
	private textures: Texture[];
	private sizes: [number, number][];
	private textureReady: boolean[];
	private pendingGoToIndex: number | null = null;
	private morphCompleteCallback: (() => void) | null = null;
	private readyWaiters: Array<() => void> = [];
	private lastCssWidth = 0;
	private lastCssHeight = 0;
	private resizeObserver: ResizeObserver;
	private raf = 0;
	private boundLoop: (t: number) => void;
	private boundContextLost: (e: Event) => void;
	private destroyed = false;

	private constructor(container: HTMLElement, config: MorphEngineConfig) {
		const alpha = config.alpha === true;
		const renderer = tryCreateOglRenderer({
			alpha,
			antialias: true,
			dpr: Math.min(window.devicePixelRatio || 1, config.dprCap ?? 2),
		});
		if (!renderer) {
			throw new Error("MorphEngine: WebGL unavailable");
		}

		this.container = container;
		this.items = config.items;
		this.getOptions = config.getOptions;
		this.onIndexChange = config.onIndexChange;
		this.reducedMotion = config.reducedMotion;
		this.current = config.startIndex;
		this.shownIndex = config.startIndex;

		this.renderer = renderer;
		this.gl = this.renderer.gl;
		if (alpha) {
			this.gl.clearColor(0, 0, 0, 0);
		} else {
			this.gl.clearColor(0.05, 0.05, 0.06, 1);
		}

		this.canvas = this.gl.canvas as HTMLCanvasElement;
		this.canvas.className = "block h-full w-full";
		container.appendChild(this.canvas);

		this.geometry = new Triangle(this.gl);

		this.textures = this.items.map(() => makeFallbackTexture(this.gl));
		this.sizes = this.items.map(() => [1, 1] as [number, number]);
		this.textureReady = this.items.map(() => false);

		const opts = this.getOptions();
		this.program = new Program(this.gl, {
			vertex: morphVertexShader,
			fragment: morphFragmentShader,
			uniforms: {
				tCurrent: { value: this.textures[this.current] },
				tNext: { value: this.textures[this.current] },
				uResolution: { value: [1, 1] },
				uCurrentSize: { value: this.sizes[this.current] },
				uNextSize: { value: this.sizes[this.current] },
				uProgress: { value: 0 },
				uDir: { value: 1 },
				uMode: { value: MORPH_TRANSITIONS[opts.transition] ?? 0 },
				uIntensity: { value: opts.intensity },
				uScale: { value: opts.scale },
				uAberration: { value: opts.aberration },
				uDrift: { value: opts.drift },
				uTime: { value: 0 },
				uReduce: { value: this.reducedMotion ? 1 : 0 },
				uPointer: { value: [0.5, 0.5] },
				uOverlay: { value: morphHexToRgb(opts.overlayColor) },
			},
		});

		this.mesh = new Mesh(this.gl, {
			geometry: this.geometry,
			program: this.program,
		});

		this.boundContextLost = this.onContextLost.bind(this);
		this.canvas.addEventListener(
			"webglcontextlost",
			this.boundContextLost,
			false,
		);

		this.resizeObserver = new ResizeObserver(() => this.resize());
		this.resizeObserver.observe(container);
		this.resize();

		this.loadTextures();

		this.boundLoop = this.loop.bind(this);
		this.raf = requestAnimationFrame(this.boundLoop);
	}

	static create(
		container: HTMLElement,
		config: MorphEngineConfig,
	): MorphEngine | null {
		try {
			return new MorphEngine(container, config);
		} catch {
			return null;
		}
	}

	get index(): number {
		return this.current;
	}

	get isAnimating(): boolean {
		return this.animating;
	}

	/** True once every item texture has loaded (or failed — still marks ready). */
	get areTexturesReady(): boolean {
		return (
			this.textureReady.length > 0 && this.textureReady.every(Boolean)
		);
	}

	isTextureReady(index: number): boolean {
		return this.textureReady[this.wrap(index)] === true;
	}

	/** Resolves when all textures have settled (load or error). */
	whenTexturesReady(): Promise<void> {
		if (this.areTexturesReady) {
			return Promise.resolve();
		}
		return new Promise((resolve) => {
			this.readyWaiters.push(resolve);
		});
	}

	private markTextureReady(index: number): void {
		if (this.textureReady[index]) {
			return;
		}
		this.textureReady[index] = true;
		if (this.areTexturesReady) {
			const waiters = this.readyWaiters.splice(0);
			for (const resolve of waiters) {
				resolve();
			}
		}
		this.flushPendingGoTo();
	}

	private flushPendingGoTo(): void {
		if (this.pendingGoToIndex == null) {
			return;
		}
		const target = this.pendingGoToIndex;
		if (
			!this.isTextureReady(this.current) ||
			!this.isTextureReady(target)
		) {
			return;
		}
		this.pendingGoToIndex = null;
		this.goToIndex(target);
	}

	private loadTextures(): void {
		this.items.forEach((item, index) => {
			const img = new Image();
			// Same-origin public paths must not set crossOrigin — it can fail
			// the decode and leave the near-black fallback visible.
			const isRemote = /^https?:\/\//i.test(item.image);
			if (isRemote) {
				img.crossOrigin = "anonymous";
			}
			img.src = item.image;
			img.onload = () => {
				const texture = new Texture(this.gl, {
					generateMipmaps: false,
				});
				texture.image = img;
				this.textures[index] = texture;
				this.sizes[index] = [
					img.naturalWidth || 1,
					img.naturalHeight || 1,
				];
				if (index === this.current && !this.animating) {
					this.program.uniforms.tCurrent.value = texture;
					this.program.uniforms.uCurrentSize.value =
						this.sizes[index];
				}
				this.markTextureReady(index);
			};
			img.onerror = () => {
				this.markTextureReady(index);
			};
		});
	}

	private resize(): void {
		const rect = this.container.getBoundingClientRect();
		const w = Math.max(rect.width, 1);
		const h = Math.max(rect.height, 1);
		if (
			Math.abs(w - this.lastCssWidth) < 0.5 &&
			Math.abs(h - this.lastCssHeight) < 0.5
		) {
			return;
		}
		this.lastCssWidth = w;
		this.lastCssHeight = h;
		this.renderer.setSize(w, h);
		this.program.uniforms.uResolution.value = [
			this.gl.canvas.width,
			this.gl.canvas.height,
		];
		// Paint immediately — setSize clears the buffer; waiting for RAF flashes
		// clearColor (black or empty) for one or more frames.
		this.renderer.render({ scene: this.mesh });
	}

	private syncOptions(): void {
		const opts = this.getOptions();
		this.program.uniforms.uMode.value =
			MORPH_TRANSITIONS[opts.transition] ?? 0;
		this.program.uniforms.uIntensity.value = opts.intensity;
		this.program.uniforms.uScale.value = opts.scale;
		this.program.uniforms.uAberration.value = opts.aberration;
		this.program.uniforms.uDrift.value = opts.drift;
		this.program.uniforms.uOverlay.value = morphHexToRgb(opts.overlayColor);
	}

	private loop(t: number): void {
		this.program.uniforms.uTime.value = t * 0.001;
		if (!this.dragging && !this.animating) {
			this.syncOptions();
		}
		this.renderer.render({ scene: this.mesh });
		this.raf = requestAnimationFrame(this.boundLoop);
	}

	private wrap(i: number): number {
		const n = this.items.length;
		return ((i % n) + n) % n;
	}

	private prepareNext(dir: number): number {
		const target = this.wrap(this.current + dir);
		this.program.uniforms.tCurrent.value = this.textures[this.current];
		this.program.uniforms.uCurrentSize.value = this.sizes[this.current];
		this.program.uniforms.tNext.value = this.textures[target];
		this.program.uniforms.uNextSize.value = this.sizes[target];
		this.program.uniforms.uDir.value = dir;
		return target;
	}

	goTo(dir: number): void {
		if (this.animating || this.dragging || this.items.length < 2) {
			return;
		}
		const opts = this.getOptions();
		if (!opts.loop) {
			const raw = this.current + dir;
			if (raw < 0 || raw > this.items.length - 1) {
				return;
			}
		}
		this.syncOptions();
		const target = this.prepareNext(dir);
		this.animating = true;
		this.announce(target);
		const duration = this.reducedMotion
			? Math.min(opts.duration, 0.4)
			: opts.duration;
		this.tween = gsap.fromTo(
			this.program.uniforms.uProgress,
			{ value: 0 },
			{
				value: 1,
				duration,
				ease: opts.ease,
				onComplete: () => this.commit(target),
			},
		);
	}

	/** Absolute index morph (MorphImage / indicators). */
	goToIndex(
		index: number,
		options?: {
			onComplete?: () => void;
		},
	): void {
		if (this.dragging || this.items.length < 2) {
			options?.onComplete?.();
			return;
		}
		const target = this.wrap(index);
		if (target === this.current && !this.animating) {
			this.pendingGoToIndex = null;
			options?.onComplete?.();
			return;
		}
		if (options?.onComplete) {
			this.morphCompleteCallback = options.onComplete;
		}
		if (this.animating) {
			this.pendingGoToIndex = target;
			return;
		}
		if (!this.isTextureReady(this.current) || !this.isTextureReady(target)) {
			this.pendingGoToIndex = target;
			return;
		}
		const dir = target > this.current ? 1 : -1;
		this.syncOptions();
		this.program.uniforms.tCurrent.value = this.textures[this.current];
		this.program.uniforms.uCurrentSize.value = this.sizes[this.current];
		this.program.uniforms.tNext.value = this.textures[target];
		this.program.uniforms.uNextSize.value = this.sizes[target];
		this.program.uniforms.uDir.value = dir;
		this.animating = true;
		this.announce(target);
		const opts = this.getOptions();
		const duration = this.reducedMotion
			? Math.min(opts.duration, 0.4)
			: opts.duration;
		this.tween = gsap.fromTo(
			this.program.uniforms.uProgress,
			{ value: 0 },
			{
				value: 1,
				duration,
				ease: opts.ease,
				onComplete: () => this.commit(target),
			},
		);
	}

	private announce(index: number): void {
		if (index === this.shownIndex) {
			return;
		}
		this.shownIndex = index;
		this.onIndexChange?.(index);
	}

	private commit(target: number): void {
		this.current = target;
		this.program.uniforms.tCurrent.value = this.textures[target];
		this.program.uniforms.uCurrentSize.value = this.sizes[target];
		this.program.uniforms.uProgress.value = 0;
		this.animating = false;
		this.tween = null;
		this.announce(target);
		const onComplete = this.morphCompleteCallback;
		this.morphCompleteCallback = null;
		onComplete?.();
		this.flushPendingGoTo();
	}

	next(): void {
		this.goTo(1);
	}

	prev(): void {
		this.goTo(-1);
	}

	setPointer(x: number, y: number): void {
		this.program.uniforms.uPointer.value = [x, y];
	}

	beginDrag(): boolean {
		if (this.animating || this.items.length < 2) {
			return false;
		}
		this.dragging = true;
		this.dragDir = 0;
		this.syncOptions();
		return true;
	}

	drag(ndx: number): void {
		if (!this.dragging) {
			return;
		}
		const opts = this.getOptions();
		const dir = ndx < 0 ? 1 : -1;
		if (!opts.loop) {
			const raw = this.current + dir;
			if (raw < 0 || raw > this.items.length - 1) {
				this.program.uniforms.uProgress.value = 0;
				return;
			}
		}
		if (dir !== this.dragDir) {
			this.dragDir = dir;
			this.prepareNext(dir);
		}
		const progress = Math.min(Math.abs(ndx), 1);
		this.program.uniforms.uProgress.value = progress;
		this.announce(
			progress > 0.5 ? this.wrap(this.current + dir) : this.current,
		);
	}

	endDrag(): void {
		if (!this.dragging) {
			return;
		}
		this.dragging = false;
		const p = this.program.uniforms.uProgress.value as number;
		if (this.dragDir === 0) {
			return;
		}
		const target = this.wrap(this.current + this.dragDir);
		const duration = this.reducedMotion ? 0.3 : 0.5;
		this.animating = true;
		if (p > 0.4) {
			this.announce(target);
			this.tween = gsap.to(this.program.uniforms.uProgress, {
				value: 1,
				duration,
				ease: "power2.out",
				onComplete: () => this.commit(target),
			});
		} else {
			this.announce(this.current);
			this.tween = gsap.to(this.program.uniforms.uProgress, {
				value: 0,
				duration,
				ease: "power2.out",
				onComplete: () => {
					this.animating = false;
					this.tween = null;
				},
			});
		}
	}

	private onContextLost(e: Event): void {
		e.preventDefault();
		cancelAnimationFrame(this.raf);
	}

	destroy(): void {
		if (this.destroyed) {
			return;
		}
		this.destroyed = true;
		cancelAnimationFrame(this.raf);
		this.raf = 0;
		if (this.tween) {
			this.tween.kill();
			this.tween = null;
		}
		this.resizeObserver.disconnect();
		this.canvas.removeEventListener(
			"webglcontextlost",
			this.boundContextLost,
		);
		for (const tex of this.textures) {
			if (tex?.texture) {
				this.gl.deleteTexture(tex.texture);
			}
		}
		if (this.program?.program) {
			this.gl.deleteProgram(this.program.program);
		}
		const ext = this.gl.getExtension("WEBGL_lose_context");
		if (ext) {
			ext.loseContext();
		}
		if (this.canvas.parentNode) {
			this.canvas.parentNode.removeChild(this.canvas);
		}
	}
}
